import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus, Store, Truck, DollarSign } from 'lucide-react';

export const DashboardAnalytics = ({ dashAnalytics, formatCurrency }) => {
  const navigate = useNavigate();

  return (
    <div className="mt-8 space-y-4" data-testid="dashboard-analytics">
      <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-zinc-500">Analytics</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Monthly Spend */}
        <div className="bg-white border border-zinc-200 p-4" data-testid="monthly-spend-card">
          <DollarSign className="w-5 h-5 text-primary mb-2" />
          <p className="text-xl font-mono font-semibold text-zinc-900">{formatCurrency(dashAnalytics.monthly_spend.this_month)}</p>
          <p className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400 mb-1">Spend This Month</p>
          {dashAnalytics.monthly_spend.pct_change !== null ? (
            <p className={`text-sm font-bold flex items-center gap-1 ${
              dashAnalytics.monthly_spend.pct_change > 0 ? 'text-destructive' :
              dashAnalytics.monthly_spend.pct_change < 0 ? 'text-success' : 'text-zinc-500'
            }`}>
              {dashAnalytics.monthly_spend.pct_change > 0 ? <TrendingUp className="w-4 h-4" /> :
               dashAnalytics.monthly_spend.pct_change < 0 ? <TrendingDown className="w-4 h-4" /> :
               <Minus className="w-4 h-4" />}
              {Math.abs(dashAnalytics.monthly_spend.pct_change)}% vs last month
            </p>
          ) : (
            <p className="text-xs text-zinc-500">No comparison data</p>
          )}
        </div>

        {/* Top Vendors (90 days) */}
        <div className="bg-white border border-zinc-200 p-4 sm:col-span-1 lg:col-span-1" data-testid="top-vendors-card">
          <Store className="w-5 h-5 text-primary mb-2" />
          <p className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400 mb-2">Top Vendors (90d)</p>
          {dashAnalytics.top_vendors.length === 0 ? (
            <p className="text-sm text-zinc-400">No recent orders</p>
          ) : (
            <div className="space-y-1.5">
              {dashAnalytics.top_vendors.map((v) => (
                <div key={v.vendor_name} className="flex items-center justify-between">
                  <span className="text-sm text-zinc-700 truncate flex-1 mr-2">{v.vendor_name}</span>
                  <span className="text-sm font-mono font-medium text-zinc-900 flex-shrink-0">{formatCurrency(v.total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Budget Health */}
        <div className="bg-white border border-zinc-200 p-4 sm:col-span-2 lg:col-span-2" data-testid="budget-health-card">
          <p className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400 mb-3">Budget Health — Active Projects</p>
          {dashAnalytics.budget_health.length === 0 ? (
            <p className="text-sm text-zinc-400">No active projects</p>
          ) : (
            <div className="space-y-2">
              {dashAnalytics.budget_health.map((p) => (
                <div key={p.project_id} data-testid={`budget-health-${p.project_id}`}>
                  <div className="flex justify-between text-xs mb-0.5">
                    <span className="font-medium text-zinc-700 truncate mr-2">{p.name}</span>
                    <span className={`font-mono font-medium flex-shrink-0 ${
                        p.status === 'red' ? 'text-red-500' :
                        p.status === 'yellow' ? 'text-amber-500' : 'text-green-600'
                      }`}>{p.pct}%</span>
                  </div>
                  <div className="h-1.5 bg-zinc-100 overflow-hidden">
                    <div
                      className={`h-full ${
                        p.status === 'red' ? 'bg-red-500' :
                        p.status === 'yellow' ? 'bg-amber-400' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(p.pct, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upcoming Deliveries */}
      {dashAnalytics.upcoming_deliveries.length > 0 && (
        <div className="bg-white border border-zinc-200 p-4" data-testid="upcoming-deliveries-card">
          <div className="flex items-center gap-2 mb-3">
            <Truck className="w-4 h-4 text-primary" />
            <p className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400">Upcoming Deliveries (Next 7 Days)</p>
          </div>
          <div className="space-y-1.5">
            {dashAnalytics.upcoming_deliveries.map((d) => (
              <button
                key={d.order_id}
                onClick={() => navigate(`/projects/${d.project_id}`)}
                className="w-full flex items-center gap-3 p-2 border border-zinc-100 bg-zinc-50 hover:bg-zinc-100 text-left text-sm transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-zinc-900">{d.vendor_name}</span>
                  <span className="text-zinc-400"> — {d.project_name}</span>
                </div>
                <span className="font-mono font-medium text-zinc-900 flex-shrink-0">{formatCurrency(d.total)}</span>
                <span className="text-[10px] font-mono text-zinc-400 flex-shrink-0">
                  {new Date(d.expected_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
